import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { contacts } from '../utils/api';
import { getContactTypeLabel, getContactTypeColor } from '../features/contacts/utils';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';

export default function ContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const { user } = useAuth();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchContact();
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const params = userId ? { userId } : {};
      const data = await contacts.getById(id, params);
      setContact(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch contact details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const params = userId ? { userId } : {};
      await contacts.delete(id, params);
      navigate(userId ? `/contacts?userId=${userId}` : '/contacts');
    } catch (err) {
      setError(err.message || 'Failed to delete contact');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!contact) {
    return null;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold">
                {contact.display_name}
                {contact.company_name && contact.company_name !== contact.display_name && ` (${contact.company_name})`}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getContactTypeColor(contact.contact_type)}`}>
                {getContactTypeLabel(contact.contact_type)}
              </span>
            </div>
            <div className="flex space-x-3">
              <Link
                to={`/contacts/${id}/edit${userId ? `?userId=${userId}` : ''}`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Edit
              </Link>
              <button
                onClick={() => setDeleteConfirm(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Website</dt>
                  <dd className="mt-1">
                    {contact.website ? (
                      <a 
                        href={contact.website?.startsWith('http') ? contact.website : `https://${contact.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {contact.website}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800">
                        {contact.phone}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-gray-900">
                    {contact.address || <span className="text-gray-400">-</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Secondary Address</dt>
                  <dd className="mt-1 text-gray-900">
                    {contact.secondary_address || <span className="text-gray-400">-</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Source</dt>
                  <dd className="mt-1 text-gray-900">
                    {contact.source || <span className="text-gray-400">-</span>}
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Transaction Summary</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Transactions</dt>
                  <dd className="mt-1 text-gray-900">
                    {contact.transactions?.length || '0'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Sales</dt>
                  <dd className="mt-1 text-gray-900">
                    {(() => {
                      const totalSales = contact.transactions?.reduce((sum, t) => {
                        if (t.transaction_type === 'sale') {
                          return sum + t.coins.reduce((coinSum, coin) => coinSum + (coin.sale_price || 0), 0);
                        }
                        return sum;
                      }, 0) || 0;
                      
                      const formattedSales = totalSales.toLocaleString('en-US', { 
                        minimumFractionDigits: 0, 
                        maximumFractionDigits: 2
                      });

                      return totalSales > 0 ? (
                        <Link 
                          to={`/transactions?type=sales&contact_name=${encodeURIComponent(contact.display_name)}${userId ? `&userId=${userId}` : ''}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ${formattedSales}
                        </Link>
                      ) : (
                        <span>${formattedSales}</span>
                      );
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Purchases</dt>
                  <dd className="mt-1 text-gray-900">
                    {(() => {
                      const totalPurchases = contact.transactions?.reduce((sum, t) => {
                        if (t.transaction_type === 'purchase') {
                          return sum + t.coins.reduce((coinSum, coin) => coinSum + (coin.purchase_price || 0), 0);
                        }
                        return sum;
                      }, 0) || 0;
                      
                      const formattedPurchases = totalPurchases.toLocaleString('en-US', { 
                        minimumFractionDigits: 0, 
                        maximumFractionDigits: 2
                      });

                      return totalPurchases > 0 ? (
                        <Link 
                          to={`/transactions?type=purchases&contact_name=${encodeURIComponent(contact.display_name)}${userId ? `&userId=${userId}` : ''}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ${formattedPurchases}
                        </Link>
                      ) : (
                        <span>${formattedPurchases}</span>
                      );
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Transaction</dt>
                  <dd className="mt-1 text-gray-900">
                    {contact.last_transaction_date ? formatDate(contact.last_transaction_date, { user }) : '-'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="mt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Notes</h2>
            {contact.notes ? (
              <p className="text-gray-600 whitespace-pre-wrap">{contact.notes}</p>
            ) : (
              <p className="text-gray-400">-</p>
            )}
          </div>

          {contact.transactions && contact.transactions.length > 0 && (
            <div className="mt-8 space-y-8">
              {/* Sales Transactions */}
              {contact.transactions.some(t => t.transaction_type === 'sale') && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Sales History</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Coins
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {contact.transactions
                          .filter(t => t.transaction_type === 'sale')
                          .map((transaction) => (
                            <tr key={transaction.id}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(transaction.transaction_date, { user })}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                ${transaction.coins.reduce((sum, coin) => sum + (coin.sale_price || 0), 0)
                                  .toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                                {transaction.coins.length}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                                <button
                                  onClick={() => navigate(`/transactions?view=${transaction.id}&transaction_type=sale${userId ? `&userId=${userId}` : ''}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Purchase Transactions */}
              {contact.transactions.some(t => t.transaction_type === 'purchase') && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Purchase History</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Coins
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {contact.transactions
                          .filter(t => t.transaction_type === 'purchase')
                          .map((transaction) => (
                            <tr key={transaction.id}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(transaction.transaction_date, { user })}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                ${transaction.coins.reduce((sum, coin) => sum + (coin.purchase_price || 0), 0)
                                  .toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                                {transaction.coins.length}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                                <button
                                  onClick={() => navigate(`/transactions?view=${transaction.id}&transaction_type=purchase${userId ? `&userId=${userId}` : ''}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Contact</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this contact? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
